import { lazy, Suspense } from 'react';
import { format } from 'date-fns';
import { User, Sparkles, Wrench } from 'lucide-react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import type { AIMessage } from '@/types/ai-assistant.types';
import { AIAssistantToolCard } from './ai-assistant-tool-card';
import './ai-assistant.css';

const GenerativeUIRenderer = lazy(() =>
  import('./generative-ui/generative-ui-renderer').then((m) => ({
    default: m.GenerativeUIRenderer,
  })),
);

const markdownComponents: Components = {
  h1: ({ children }) => (
    <h1 className="text-2xl font-bold mt-6 mb-4 first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-xl font-bold mt-5 mb-3 first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-lg font-semibold mt-4 mb-2 first:mt-0">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-base font-semibold mt-3 mb-2 first:mt-0">{children}</h4>
  ),
  h5: ({ children }) => (
    <h5 className="text-sm font-semibold mt-3 mb-2 first:mt-0">{children}</h5>
  ),
  h6: ({ children }) => (
    <h6 className="text-sm font-semibold mt-3 mb-2 first:mt-0">{children}</h6>
  ),
  p: ({ children }) => (
    <p className="mb-4 last:mb-0 leading-7">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="list-disc list-outside mb-4 space-y-2 ml-6">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-outside mb-4 space-y-2 ml-6">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="leading-7 pl-2">{children}</li>
  ),
  code: ({ className, children, ...props }) => {
    const isInline = !className?.includes('language-');
    if (isInline) {
      return (
        <code
          className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono border"
          {...props}
        >
          {children}
        </code>
      );
    }
    return (
      <code
        className={cn(
          'block bg-muted p-4 rounded-lg text-sm font-mono overflow-x-auto mb-4 border',
          className
        )}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="mb-4 overflow-x-auto">{children}</pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-primary pl-4 italic my-4 text-muted-foreground">
      {children}
    </blockquote>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary hover:underline font-medium"
    >
      {children}
    </a>
  ),
  hr: () => <hr className="my-6 border-border" />,
  table: ({ children }) => (
    <div className="overflow-x-auto mb-4">
      <table className="min-w-full border-collapse border border-border">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-muted">{children}</thead>
  ),
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => (
    <tr className="border-b border-border">{children}</tr>
  ),
  th: ({ children }) => (
    <th className="border border-border px-4 py-2 text-left font-semibold">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-border px-4 py-2">{children}</td>
  ),
  strong: ({ children }) => (
    <strong className="font-bold">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  del: ({ children }) => (
    <del className="line-through text-muted-foreground">{children}</del>
  ),
};

interface AIAssistantMessageProps {
  readonly message: AIMessage;
}

export function AIAssistantMessage({ message }: Readonly<AIAssistantMessageProps>) {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';

  if (isSystem) {
    return (
      <div className="flex justify-center">
        <div className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex gap-3', isUser && 'flex-row-reverse')}>
      {/* Avatar */}
      <div className={cn(
        'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
        isUser ? 'bg-primary text-primary-foreground' : 'bg-muted'
      )}>
        {isUser ? (
          <User className="h-4 w-4" />
        ) : (
          <Sparkles className="h-4 w-4 text-primary" />
        )}
      </div>

      {/* Message Content */}
      <div className={cn('flex-1 space-y-2', isUser && 'flex flex-col items-end')}>
        <div className={cn(
          'rounded-lg px-4 py-2 max-w-[85%]',
          isUser
            ? 'bg-primary text-primary-foreground ml-auto'
            : 'bg-muted'
        )}>
          {isUser ? (
            <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
          ) : (
            <div className="text-sm ai-message-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw, rehypeSanitize]}
                components={markdownComponents}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Tool Execution */}
        {message.toolCall && (
          <div className="space-y-2 max-w-[85%]">
            <AIAssistantToolCard tool={message.toolCall} />
          </div>
        )}

        {/* Tools Used */}
        {!isUser && message.toolsUsed && message.toolsUsed.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap max-w-[85%]">
            <Wrench className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            {message.toolsUsed.map((tool) => (
              <Badge
                key={tool}
                variant="outline"
                className="text-[10px] px-1.5 py-0 h-5 font-normal"
              >
                {tool}
              </Badge>
            ))}
          </div>
        )}

        {/* Generative UI Blocks */}
        {message.uiBlocks && message.uiBlocks.length > 0 && (
          <Suspense fallback={
            <div className="max-w-[95%] animate-pulse rounded-lg bg-muted h-24" />
          }>
            <div className="max-w-[95%]">
              <GenerativeUIRenderer blocks={message.uiBlocks} />
            </div>
          </Suspense>
        )}

        {/* Timestamp */}
        <div className={cn(
          'text-xs text-muted-foreground',
          isUser && 'text-right'
        )}>
          {format(new Date(message.timestamp), 'h:mm a')}
        </div>
      </div>
    </div>
  );
}
